import {
	SL_TP_MAX_SIZE_CROSS_MARGIN,
	V3SynthMarketKey,
	V3_SYNTH_MARKET_KEYS,
	ZERO_WEI,
	v3SynthKeyToV2Asset,
} from '@kwenta/sdk/constants'
import {
	AssetKey,
	ConditionalOrderTypeEnum,
	FuturesOrderType,
	PerpsMarketV3,
	PerpsV3Liquidation,
	PerpsV3Trade,
	PositionSide,
	SupportedNetworkIds,
} from '@kwenta/sdk/types'
import {
	MarketKeyByAsset,
	calculateDesiredFillPrice,
	floorNumber,
	formatCurrency,
	formatOrderDisplayType,
	getDefaultPriceImpact,
	getDisplayAsset,
	orderTypeFromCondition,
} from '@kwenta/sdk/utils'
import Wei, { wei } from '@kwenta/wei'
import { createSelector } from '@reduxjs/toolkit'
import {
	ConditionOrderTableItem,
	FuturesPositionTablePosition,
	FuturesPositionTablePositionWithDetails,
} from 'types/futures'

import {
	V3_CONDITIONAL_ORDERS_ENABLED,
	DEFAULT_DELAYED_CANCEL_BUFFER,
	MARGIN_ENGINE_ENABLED,
} from 'constants/defaults'
import { REQUIRED_MARGIN_RATIO } from 'constants/perpsV3'
import { selectShowModal, selectShowPositionModal } from 'state/app/selectors'
import {
	selectBalances,
	selectSynthV3BalancesAndAllowances,
	selectTokenAllowances,
	selectUsdcBalance,
} from 'state/balances/selectors'
import { KEEPER_USD_GAS_FEE, ZERO_STATE_MARGIN_INFO } from 'state/constants'
import { selectOffchainPricesInfo, selectPrices } from 'state/prices/selectors'
import { RootState } from 'state/store'
import { FetchStatus } from 'state/types'
import { selectProviderNetwork, selectSignerWallet, selectWallet } from 'state/wallet/selectors'
import {
	calculateKeeperFeesForOrders,
	calculateV3LiqPrice,
	unserializeFuturesVolumes,
	unserializePerpsV3PositionHistory,
	unserializeV3Liquidations,
	unserializeV3Market,
	unserializeV3Positions,
	unserializeV3Trades,
} from 'utils/futures'

import { unserializeCrossMarginTradePreview } from '../../../utils/futures'
import {
	selectPerpsProvider,
	selectMarketAsset,
	selectMarketIndexPrice,
	selectMarketPriceInfo,
	selectPortfolioChartType,
	selectSelectedPortfolioTimeframe,
	selectUserInfoShowAllMarkets,
	selectSlTpTradeInputs,
	selectTradeOrderType,
	selectTradePanelInputs,
	selectClosePositionOrderInputs,
	selectLeverageSide,
} from '../common/selectors'
import { CrossMarginRiskStatus, MarkPriceInfos, MarkPrices } from '../common/types'
import { PerpsProvider } from '../types'

export const selectV3MarketKey = createSelector(
	(state: RootState) => state.futures.selectedMarketAsset.snx_v3,
	(marketAsset) => MarketKeyByAsset[marketAsset]
)

export const selectV3Markets = createSelector(
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3,
	(network, crossMargin) => {
		const markets = crossMargin.markets[network] ? crossMargin.markets[network] : []
		return markets.map(unserializeV3Market)
	}
)

export const selectMargineEngineEnabled = createSelector(selectProviderNetwork, (network) => {
	return (
		(network === SupportedNetworkIds.BASE_SEPOLIA || SupportedNetworkIds.BASE_MAINNET) &&
		MARGIN_ENGINE_ENABLED
	)
})

export const selectCondOrdersEnabled = createSelector(
	selectPerpsProvider,
	selectMargineEngineEnabled,
	(futuresType, margineEngineEnabled) => {
		return (
			futuresType === PerpsProvider.SNX_V2 ||
			(V3_CONDITIONAL_ORDERS_ENABLED && margineEngineEnabled)
		)
	}
)

export const selectMarkPricesV3 = createSelector(
	selectV3Markets,
	selectPrices,
	(markets, prices) => {
		const markPrices: MarkPrices = {}
		return markets.reduce((acc, market) => {
			const price = prices[market.asset]?.offChain ?? wei(0)
			return {
				...acc,
				[market.marketKey]: wei(price).mul(
					wei(market.marketSkew).div(market.settings.skewScale).add(1)
				),
			}
		}, markPrices)
	}
)

export const selectPerpsV3MarketVolumes = createSelector(
	(state: RootState) => state.snxPerpsV3.dailyMarketVolumes,
	(dailyMarketVolumes) => unserializeFuturesVolumes(dailyMarketVolumes)
)

export const selectV3SelectedMarketInfo = createSelector(
	selectV3Markets,
	selectV3MarketKey,
	(markets, selectedMarket) => {
		return markets.find((market) => market.marketKey === selectedMarket)
	}
)

export const selectV3SelectedMarketId = createSelector(
	selectV3SelectedMarketInfo,
	(marketInfo) => marketInfo?.marketId
)

export const selectSignerSupportsCrossMargin = (state: RootState) =>
	state.wallet.walletNetworkId === SupportedNetworkIds.BASE_SEPOLIA ||
	state.wallet.walletNetworkId === SupportedNetworkIds.BASE_MAINNET

export const selectProviderSupportsCrossMargin = (state: RootState) =>
	state.wallet.clientNetworkId === SupportedNetworkIds.BASE_SEPOLIA ||
	state.wallet.clientNetworkId === SupportedNetworkIds.BASE_MAINNET

export const selectCrossMarginAccount = createSelector(
	selectWallet,
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3,
	(wallet, network, crossMargin) => {
		const id = wallet ? crossMargin.accounts[network]?.[wallet]?.account : undefined
		return id ? BigInt(id) : undefined
	}
)

export const selectAccountContext = createSelector(
	selectWallet,
	selectProviderNetwork,
	selectCrossMarginAccount,
	(wallet, network, accountId) => {
		return { wallet, network, accountId }
	}
)

export const selectCrossMarginAccountData = createSelector(
	selectWallet,
	selectProviderNetwork,
	selectProviderSupportsCrossMargin,
	(state: RootState) => state.snxPerpsV3,
	(wallet, network, supportedNetwork, crossMargin) => {
		return wallet && supportedNetwork ? crossMargin.accounts[network]?.[wallet] : null
	}
)

export const selectCrossMarginAccountDataBySignerWallet = createSelector(
	selectSignerWallet,
	selectProviderNetwork,
	selectProviderSupportsCrossMargin,
	(state: RootState) => state.snxPerpsV3,
	(wallet, network, supportedNetwork, crossMargin) => {
		return wallet && supportedNetwork ? crossMargin.accounts[network]?.[wallet] : null
	}
)

export const selectMarginEnginePermitted = createSelector(
	selectCrossMarginAccountData,
	selectMargineEngineEnabled,
	(account, engineEnabled) => {
		return engineEnabled && !!account?.marginEnginePermitted
	}
)

export const selectCrossMarginMarginInfo = createSelector(
	selectCrossMarginAccountData,
	(account) => {
		const {
			availableMargin,
			withdrawableMargin,
			requiredInitialMargin,
			requiredMaintenanceMargin,
			maxLiquidationReward,
		} = account ? account.marginInfo : ZERO_STATE_MARGIN_INFO

		return {
			availableMargin: wei(availableMargin).gt(0) ? wei(availableMargin) : wei(0),
			withdrawableMargin: wei(withdrawableMargin),
			requiredInitialMargin: wei(requiredInitialMargin),
			maxLiquidationReward: wei(maxLiquidationReward),
			requiredMaintenanceMargin: wei(requiredMaintenanceMargin).gt(1)
				? wei(requiredMaintenanceMargin)
				: wei(0),
		}
	}
)

export const selectCrossMarginPendingOrders = createSelector(
	selectCrossMarginAccountData,
	(account) => {
		const orders = account?.conditionalOrders ?? []
		return orders.filter((o) => o.status === 'Pending')
	}
)

export const selectReservedMarginForOrders = createSelector(
	selectCrossMarginPendingOrders,
	selectV3Markets,
	(pendingOrders, markets) => {
		const ordersRequiringMargin = pendingOrders.filter((o) => !o.orderDetails.isReduceOnly)

		const totalReserved = ordersRequiringMargin.reduce<Wei>((acc, order) => {
			const market = markets.find((m) => m.marketId === order.orderDetails.marketId)
			if (!market) return acc

			const orderPrice =
				order.decodedConditions.isPriceAbove ?? order.decodedConditions.isPriceBelow
			if (!orderPrice) return acc

			const notionalSize = wei(order.orderDetails.sizeDelta).abs().mul(wei(orderPrice))

			return acc.add(notionalSize.mul(REQUIRED_MARGIN_RATIO))
		}, wei(0))
		return totalReserved
	}
)

export const selectCrossMarginAvailableMargin = createSelector(
	selectCrossMarginMarginInfo,
	(marginInfo) => {
		return marginInfo.availableMargin
	}
)

export const selectMaintenanceMarginRatio = createSelector(
	selectCrossMarginMarginInfo,
	({ availableMargin, requiredMaintenanceMargin }) => {
		// Using > 1 as requiredMaintenanceMargin is always at least 1

		const ratio = requiredMaintenanceMargin.gt(1)
			? availableMargin.gt(0)
				? requiredMaintenanceMargin.div(availableMargin)
				: wei(1)
			: wei(0)
		const riskStatus = ratio.gt(0.75)
			? CrossMarginRiskStatus.High
			: ratio.gt(0.5)
			? CrossMarginRiskStatus.Moderate
			: CrossMarginRiskStatus.Low
		return { ratio, riskStatus }
	}
)

export const selectV3Spenders = createSelector(
	(state: RootState) => state.snxPerpsV3,
	(crossMargin) => crossMargin.perpsV3Spenders
)

type DepositAllowances = { marketProxy: Wei; marginEngine: Wei; spotProxy: Wei }

export const selectDepositAllowances = createSelector(
	selectV3Spenders,
	selectSynthV3BalancesAndAllowances,
	selectTokenAllowances,
	(spenders, balancesAndAllowances, tokenAllowances) => {
		if (!spenders) return {}
		const synthAllowances = Object.keys(balancesAndAllowances).reduce<
			Partial<Record<V3SynthMarketKey, DepositAllowances>>
		>((acc, asset) => {
			const key = asset as V3SynthMarketKey
			acc[key] = {
				marketProxy: spenders.marketProxy
					? balancesAndAllowances[key]?.allowances[spenders.marketProxy] ?? wei(0)
					: wei(0),
				marginEngine: spenders.marginEngine
					? balancesAndAllowances[key]?.allowances[spenders.marginEngine] ?? wei(0)
					: wei(0),
				spotProxy: spenders.spotProxy
					? balancesAndAllowances[key]?.allowances[spenders.spotProxy] ?? wei(0)
					: wei(0),
			}
			return acc
		}, {})

		const tokenAllowancesFormatted = Object.keys(tokenAllowances).reduce<
			Partial<Record<string, DepositAllowances>>
		>((acc, asset) => {
			acc[asset] = {
				marketProxy: spenders.marketProxy
					? tokenAllowances[asset][spenders.marketProxy] ?? wei(0)
					: wei(0),
				marginEngine: spenders.marginEngine
					? tokenAllowances[asset][spenders.marginEngine] ?? wei(0)
					: wei(0),
				spotProxy: spenders.spotProxy
					? tokenAllowances[asset][spenders.spotProxy] ?? wei(0)
					: wei(0),
			}
			return acc
		}, {})

		return {
			...tokenAllowancesFormatted,
			...synthAllowances,
		} as Record<string, DepositAllowances>
	}
)

export const selectCrossMarginPositionHistory = createSelector(
	selectCrossMarginAccountData,
	(accountData) => {
		return unserializePerpsV3PositionHistory(accountData?.positionHistory ?? [])
	}
)

export const selectCrossMarginLiquidatedPositions = createSelector(
	selectCrossMarginPositionHistory,
	selectV3Markets,
	(history, markets) => {
		const liquidated = history.filter((h) => h.isLiquidated)
		return liquidated.reduce<FuturesPositionTablePositionWithDetails[]>((acc, l) => {
			const market = markets.find((m) => m.marketId === l.marketId)
			const newerPosition = history.find(
				(ph) => ph.marketId === l.marketId && ph.openTimestamp > l.openTimestamp
			)
			if (market && !newerPosition) {
				acc.push({
					...l,
					market,
					details: {
						...l,
						accruedFunding: wei(0),
						owedInterest: wei(0),
						profitLoss: l.pnl,
						pnlPct: wei(0),
						canLiquidatePosition: false,
						liquidationPrice: l.exitPrice ?? wei(0),
						notionalValue: wei(0),
						isLiquidated: true,
						liquidatedAmount: l.liquidatedAmount,
						lastPrice: l.exitPrice ?? wei(0),
						initialMargin: wei(0),
						leverage: wei(0),
						marginRatio: wei(0),
						fundingIndex: 0,
						initialLeverage: wei(0),
						// TODO: Map position history from subgraph ata
						avgEntryPrice: l.avgEntryPrice,
					},
				})
			}
			return acc
		}, [])
	}
)

type CrossMarginTrade = PerpsV3Trade & {
	market: PerpsMarketV3
	txnHash?: string
	commitmentTxHash?: string
	settlementTxHash?: string
}

type CrossMarginLiquidation = PerpsV3Liquidation & {
	market: PerpsMarketV3
	txnHash?: string
}

export const selectCrossMarginMarginTransfers = createSelector(
	selectWallet,
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3,
	(wallet, network, futures) => {
		if (!wallet) return []
		const account = futures.accounts[network]?.[wallet]
		return account?.marketMarginTransfers ?? []
	}
)

export const selectV3SynthPrices = createSelector(
	selectV3Markets,
	selectPrices,
	(markets, prices) => {
		return markets.reduce<Partial<Record<AssetKey, Wei>>>((acc, market) => {
			const price = prices[market.asset]?.offChain ?? wei(0)
			return {
				...acc,
				[market.asset]: price,
			}
		}, {})
	}
)

export const selectMarkPriceInfosV3 = createSelector(
	selectV3Markets,
	selectOffchainPricesInfo,
	(markets, prices) => {
		const markPrices: MarkPriceInfos = {}
		return markets.reduce((acc, market) => {
			const price = prices[market.asset]?.price ?? wei(0)
			return {
				...acc,
				[market.marketKey]: {
					price: wei(price).mul(wei(market.marketSkew).div(market.settings.skewScale).add(1)),
					change: prices[market.asset]?.change ?? null,
				},
			}
		}, markPrices)
	}
)

export const selectCrossMarginConditionalOrders = createSelector(
	selectCrossMarginAccountData,
	(account) => {
		return account?.conditionalOrders ?? []
	}
)

export const selectCrossMarginOrderTableItems = createSelector(
	selectV3Markets,
	selectMarkPriceInfosV3,
	selectCrossMarginConditionalOrders,
	selectCrossMarginAccountData,
	(markets, priceInfos, orders, accountData) => {
		return orders
			.filter((o) => o.status === 'Pending')
			.reduce<ConditionOrderTableItem[]>((acc, order) => {
				const targetPrice =
					order.decodedConditions.isPriceAbove ?? order.decodedConditions.isPriceBelow
				const market = markets.find((m) => m.marketId === order.orderDetails.marketId)
				const position = accountData?.positions?.find((p) => p.marketId === market?.marketId)
				const sizeDelta = wei(order.orderDetails.sizeDelta)
				const notionalSize = wei(order.orderDetails.sizeDelta).abs().mul(wei(targetPrice))
				const reservedMargin = order.orderDetails.isReduceOnly
					? wei(0)
					: notionalSize.mul(REQUIRED_MARGIN_RATIO)

				if (!market || !targetPrice) return acc
				const priceInfo = priceInfos[market.marketKey]
				const orderType = orderTypeFromCondition(position?.side, order)
				const formattedOrder: ConditionOrderTableItem = {
					id: Number(order.id),
					createdAt: new Date(order.createdAt),
					status: order.status,
					nonce: Number(order.nonce),
					updatedAt: order.updatedAt ? new Date(order.updatedAt) : undefined,
					account: order.orderDetails.accountId,
					marketName: market.marketName,
					asset: market.asset,
					marketKey: market.marketKey,
					size: sizeDelta,
					maxExecutorFee: wei(order.maxExecutorFee),
					targetPrice: wei(targetPrice),
					desiredFillPrice: wei(order.orderDetails.acceptablePrice),
					sizeTxt: sizeDelta.abs().eq(SL_TP_MAX_SIZE_CROSS_MARGIN)
						? 'Close'
						: formatCurrency(market.asset, sizeDelta, {
								currencyKey: getDisplayAsset(market.asset) ?? '',
								suggestDecimals: true,
						  }),
					marginDelta: reservedMargin,
					orderType: orderType,
					orderTypeDisplay: formatOrderDisplayType(orderType, order.orderDetails.isReduceOnly),
					reduceOnly: order.orderDetails.isReduceOnly,
					side: sizeDelta.gt(0) ? PositionSide.LONG : PositionSide.SHORT,
					isSlTp: sizeDelta.abs().eq(SL_TP_MAX_SIZE_CROSS_MARGIN),
					currentPrice: priceInfo,
				}
				acc.push(formattedOrder)
				return acc
			}, [])
	}
)

export const selectAllCrossMarginSLTPOrders = createSelector(
	selectCrossMarginOrderTableItems,
	(orders) => {
		return orders.filter((o) => o.reduceOnly && wei(o.size).abs().eq(SL_TP_MAX_SIZE_CROSS_MARGIN))
	}
)

export const selectRequiredUsdForPendingOrders = createSelector(
	selectCrossMarginOrderTableItems,
	(orders) => {
		return calculateKeeperFeesForOrders(orders)
	}
)

export const selectKeeperUSDBalance = createSelector(selectCrossMarginAccountData, (account) => {
	return wei(account?.usdcBalance ?? 0)
})

export const selectRequiredKeeperUsdDeposit = createSelector(
	selectRequiredUsdForPendingOrders,
	selectKeeperUSDBalance,
	(requiredUsd, usdBal) => {
		return wei(requiredUsd).gt(usdBal) ? wei(requiredUsd).sub(usdBal) : ZERO_WEI
	}
)

export const selectCrossMarginPositions = createSelector(
	selectCrossMarginAccountData,
	selectV3Markets,
	selectMarkPricesV3,
	selectCrossMarginMarginInfo,
	selectCrossMarginPositionHistory,
	selectAllCrossMarginSLTPOrders,
	(
		account,
		markets,
		markPrices,
		{ availableMargin, requiredMaintenanceMargin },
		positionsHistory,
		sltpOrders
	) => {
		const positions = unserializeV3Positions(account?.positions ?? [])

		return positions.reduce<FuturesPositionTablePosition[]>((acc, position) => {
			const market = markets.find((market) => market.marketId === position.marketId)

			const history = positionsHistory.find((ph) => {
				return ph.isOpen && ph.marketId === position.marketId
			})

			if (market) {
				const markPrice = markPrices[market?.marketKey!] ?? wei(0)

				const stopLoss = sltpOrders.find((o) => {
					return o.marketKey === market.marketKey && o.orderType === ConditionalOrderTypeEnum.STOP
				})
				const takeProfit = sltpOrders.find(
					(o) => o.marketKey === market.marketKey && o.orderType === ConditionalOrderTypeEnum.LIMIT
				)

				const upnl = history
					? position.size.mul(
							history.avgEntryPrice.sub(markPrice).mul(position.side === PositionSide.LONG ? -1 : 1)
					  )
					: position.pnl

				const rPnl = history?.pnlWithFeesPaid

				const liqPrice = requiredMaintenanceMargin
					.sub(availableMargin)
					.div(position.side === PositionSide.LONG ? position.size : position.size.neg())
					.add(markPrice)

				const startNotional = position.size.abs().mul(history?.avgEntryPrice ?? 0)
				const currentNotional = position.size.abs().mul(markPrice)

				const pnlPct = startNotional.gt(0)
					? position.side === PositionSide.LONG
						? currentNotional.sub(startNotional).div(startNotional)
						: currentNotional.sub(startNotional).div(startNotional.neg())
					: wei(0)

				acc.push({
					market: market,
					stopLoss: stopLoss?.targetPrice,
					takeProfit: takeProfit?.targetPrice,
					details: {
						...position,
						accruedFunding: position.accruedFunding.add(history?.netFunding ?? ZERO_WEI),
						pnl: upnl,
						pnlPct,
						rPnl,
						canLiquidatePosition: false,
						liquidationPrice: liqPrice.lt(0) ? wei(0) : liqPrice,
						notionalValue: position.size.mul(markPrice),
						lastPrice: markPrice,
						initialMargin: wei(0),
						leverage: wei(0),
						marginRatio: wei(0),
						fundingIndex: 0,
						initialLeverage: wei(0),
						openTimestamp: history?.openTimestamp,
						avgEntryPrice: history?.avgEntryPrice,
					},
				})
			}
			return acc
		}, [])
	}
)

export const selectCrossMarginActivePositions = createSelector(
	selectCrossMarginPositions,
	(positions) => {
		const active = positions.filter((p) => !!p.details) as FuturesPositionTablePositionWithDetails[]
		return active.map((p) => ({
			...p,
			details: {
				...p.details,
				accruedFunding: p.details.accruedFunding.add(p.details.owedInterest),
			},
		}))
	}
)

export const selectSelectedCrossMarginPosition = createSelector(
	selectCrossMarginActivePositions,
	selectV3SelectedMarketInfo,
	(positions, market) => {
		const position = positions.find((p) => p.market.marketKey === market?.marketKey)
		return position
	}
)

export const selectAsyncCrossMarginOrder = createSelector(
	selectV3Markets,
	selectCrossMarginAccountData,
	(markets, account) => {
		const order = account?.pendingAsyncOrder
		if (!order) return

		const market = markets.find((m) => m.marketId === order.marketId)

		const strategy = market?.settlementStrategies.find(
			(s) => s.strategyId === order.settlementStrategyId
		)
		if (account && market && strategy) {
			const startTime = Number(order.settlementTime)
			const expirationTime = startTime + Number(strategy.settlementWindowDuration)
			const timePastExecution = Math.floor(Date.now() / 1000 - startTime)
			const executable = timePastExecution <= expirationTime
			const isStale =
				timePastExecution >
				DEFAULT_DELAYED_CANCEL_BUFFER + strategy.settlementWindowDuration.toNumber()

			const orderDetails = {
				market,
				account: Number(account.account),
				size: wei(order.sizeDelta),
				executableStartTime: startTime,
				expirationTime: expirationTime,
				marginDelta: wei(0),
				desiredFillPrice: wei(order.acceptablePrice),
				settlementWindowDuration: strategy.settlementWindowDuration.toNumber(),
				side: order.side,
				isStale: isStale,
				isExecutable: executable,
				settlementFee: strategy.settlementReward,
			}
			return orderDetails
		}
		return
	}
)

export const selectShowCrossMarginOnboard = (state: RootState) =>
	state.app.showModal?.type === 'futures_cross_margin_onboard'

export const selectWithdrawableCrossMargin = createSelector(
	selectCrossMarginActivePositions,
	selectCrossMarginMarginInfo,
	(positions, marginInfo) => {
		if (positions.length === 0) return marginInfo.availableMargin
		return marginInfo.withdrawableMargin.gt(0) ? marginInfo.withdrawableMargin : wei(0)
	}
)

export const selectEditPositionModalMarket = (state: RootState) =>
	state.app.showPositionModal?.marketKey

export const selectV3EditPositionModalInfo = createSelector(
	selectEditPositionModalMarket,
	selectCrossMarginActivePositions,
	selectPrices,
	(modalMarketKey, v3Positions, prices) => {
		const position = v3Positions.find((p) => p.market.marketKey === modalMarketKey)
		if (!position)
			return { position: null, market: null, marketPrice: wei(0), onChainPrice: wei(0) }
		const price = prices[position.market.asset]
		const marketPrice = price?.offChain
			? wei(price.offChain).mul(
					wei(position.market.marketSkew).div(position.market.settings.skewScale).add(1)
			  )
			: ZERO_WEI
		return {
			position,
			market: position.market as PerpsMarketV3,
			marketPrice: marketPrice,
			indexPrice: price.offChain,
			onChainPrice: price.onChain,
		}
	}
)

export const selectCMEditPreviewUnserialized = createSelector(
	selectCrossMarginMarginInfo,
	selectV3EditPositionModalInfo,
	(state: RootState) => state.snxPerpsV3,
	(accountMargin, { market }, crossMargin) => {
		const preview = crossMargin.previews.edit
			? unserializeCrossMarginTradePreview(crossMargin.previews.edit)
			: null
		if (!preview || !market) return
		const liqPrice = calculateV3LiqPrice(accountMargin, preview, market)
		return {
			...preview,
			liqPrice,
		}
	}
)

export const selectCrossMarginTradePreview = createSelector(
	selectTradePanelInputs,
	selectTradeOrderType,
	selectCrossMarginMarginInfo,
	selectV3SelectedMarketInfo,
	(state: RootState) => state.snxPerpsV3.previews.trade,
	({ nativeSizeDelta, orderPrice }, orderType, marginInfo, market, preview) => {
		const priceImpact = getDefaultPriceImpact(orderType)

		const desiredFillPrice = calculateDesiredFillPrice(
			nativeSizeDelta,
			wei((orderType === 'market' ? preview?.fillPrice : orderPrice.price) ?? 0),
			priceImpact
		)

		const unserializedPreview = preview ? unserializeCrossMarginTradePreview(preview) : undefined
		if (!unserializedPreview || !market) return

		const liqPrice = unserializedPreview.fillPrice
			? calculateV3LiqPrice(marginInfo, unserializedPreview, market)
			: wei(0)

		return unserializedPreview
			? {
					...unserializedPreview,
					desiredFillPrice,
					orderType,
					liqPrice,
			  }
			: undefined
	}
)

export const selectCrossMarginTradeableMargin = createSelector(
	selectCrossMarginAvailableMargin,
	selectReservedMarginForOrders,
	selectCrossMarginMarginInfo,
	(availableMargin, reserved, { requiredInitialMargin }) => {
		const tradeble = availableMargin.sub(reserved).sub(requiredInitialMargin)
		return tradeble.gte(0) ? tradeble : wei(0)
	}
)

// TODO: Improve max usd value for perps v3 (waiting for price param on requiredMarginForOrder)
export const selectMaxUsdSizeInputCrossMargin = createSelector(
	selectCrossMarginTradeableMargin,
	selectV3SelectedMarketInfo,
	(tradeableMargin, marketInfo) => {
		if (!marketInfo) return wei(0)
		return tradeableMargin.div(REQUIRED_MARGIN_RATIO)
	}
)

export const selectCrossMarginMaxLeverage = createSelector(
	selectMaxUsdSizeInputCrossMargin,
	selectCrossMarginTradeableMargin,
	(maxUsd, tradeableMargin) => {
		if (tradeableMargin.eq(0)) return wei(0)
		const leverage = wei(floorNumber(maxUsd.div(tradeableMargin), 1))
		return leverage.gt(0) ? leverage : wei(0)
	}
)

export const selectEditCMPositionModalMarketKey = (state: RootState) =>
	state.app.showPositionModal?.marketKey

export const selectPendingAsyncOrdersCount = createSelector(
	selectAsyncCrossMarginOrder,
	(order) => {
		return order ? 1 : 0
	}
)

export const selectActiveCrossMarginPositionsCount = createSelector(
	selectCrossMarginPositions,
	selectUserInfoShowAllMarkets,
	selectMarketAsset,
	(positions, userInfoShowAllMarkets, currentMarket) => {
		return userInfoShowAllMarkets
			? positions.length
			: positions.filter((p) => p.market.asset === currentMarket).length
	}
)

export const selectV3SkewAdjustedPrice = createSelector(
	selectMarketIndexPrice,
	selectV3SelectedMarketInfo,
	(price, marketInfo) => {
		if (!marketInfo?.marketSkew || !marketInfo?.settings.skewScale) return price
		return price
			? wei(price).mul(wei(marketInfo.marketSkew).div(marketInfo.settings.skewScale).add(1))
			: ZERO_WEI
	}
)

export const selectV3SkewAdjustedPriceInfo = createSelector(
	selectMarketPriceInfo,
	selectV3SelectedMarketInfo,
	(priceInfo, marketInfo) => {
		if (!marketInfo?.marketSkew || !marketInfo?.settings.skewScale) return priceInfo
		return priceInfo
			? {
					price: wei(priceInfo.price).mul(
						wei(marketInfo.marketSkew).div(marketInfo.settings.skewScale).add(1)
					),
					change: priceInfo?.change,
			  }
			: undefined
	}
)

export const selectCollateralBalances = createSelector(selectCrossMarginAccountData, (account) => {
	return account?.collateralBalances ?? {}
})

export const selectCrossMarginAccountTrades = createSelector(
	selectCrossMarginAccountData,
	selectV3Markets,
	(account, markets) => {
		const trades = unserializeV3Trades(account?.trades ?? [])
		return trades.reduce<CrossMarginTrade[]>((acc, t) => {
			const market = markets.find((m) => m.marketId === t.marketId)
			if (market) {
				acc.push({
					...t,
					market: market,
				})
			}
			return acc
		}, [])
	}
)

export const selectCrossMarginAccountLiquidations = createSelector(
	selectCrossMarginAccountData,
	selectV3Markets,
	(account, markets) => {
		const liquidations = unserializeV3Liquidations(account?.liquidations ?? [])
		return liquidations.reduce<CrossMarginLiquidation[]>((acc, t) => {
			const market = markets.find((m) => m.marketId === t.marketId)
			if (market) {
				acc.push({
					...t,
					market: market,
				})
			}
			return acc
		}, [])
	}
)

export const selectSnxV3GlobalTradesForMarket = createSelector(
	selectV3SelectedMarketId,
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3.tradesHistory,
	(marketId, network, tradesHistory) => {
		if (!marketId) return []
		return unserializeV3Trades(tradesHistory[network]?.[marketId] ?? [])
	}
)

export const selectGlobalLiquidationsForMarket = createSelector(
	selectV3SelectedMarketId,
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3.liquidationsHistory,
	(marketId, network, liquidationsHistory) => {
		if (!marketId) return []
		return unserializeV3Liquidations(liquidationsHistory[network]?.[marketId] ?? [])
	}
)

export const selectIsV3MarketCapReached = createSelector(
	selectLeverageSide,
	selectV3SelectedMarketInfo,
	selectMarketIndexPrice,
	(leverageSide, marketInfo, marketAssetRate) => {
		const maxMarketValueUSD = marketInfo?.marketLimitUsd ?? wei(0)
		const marketSize = marketInfo?.marketSize ?? wei(0)
		const marketSkew = marketInfo?.marketSkew ?? wei(0)

		return leverageSide === PositionSide.LONG
			? marketSize.add(marketSkew).div('2').abs().mul(marketAssetRate).gte(maxMarketValueUSD)
			: marketSize.sub(marketSkew).div('2').abs().mul(marketAssetRate).gte(maxMarketValueUSD)
	}
)
export const selectShowSwitchNetworkCrossMargin = createSelector(
	selectWallet,
	selectSignerSupportsCrossMargin,
	selectShowModal,
	(walletAddress, supportedNetwork, openModal) => {
		return (
			walletAddress && !supportedNetwork && openModal !== 'futures_deposit_withdraw_smart_margin'
		)
	}
)

export const selectCrossMarginQueryStatuses = (state: RootState) => state.snxPerpsV3.queryStatuses

export const selectIsFetchingCMTradePreview = createSelector(
	(state: RootState) => state.snxPerpsV3,
	(crossMargin) => {
		return crossMargin.queryStatuses.tradePreview.status === FetchStatus.Loading
	}
)

export const selectCloseCMPositionPreview = createSelector(
	selectV3EditPositionModalInfo,
	selectCrossMarginMarginInfo,
	selectClosePositionOrderInputs,
	(state: RootState) => state.snxPerpsV3,
	({ position, marketPrice, market }, marginInfo, { orderType }, crossMargin) => {
		const preview = crossMargin.previews.close
		const unserializedPreview = preview ? unserializeCrossMarginTradePreview(preview) : null
		if (unserializedPreview && market) {
			const priceImpact = getDefaultPriceImpact('market')
			const desiredFillPrice = calculateDesiredFillPrice(
				position?.details.side === PositionSide.LONG ? wei(-1) : wei(1),
				unserializedPreview.fillPrice,
				priceImpact
			)

			const liqPrice = calculateV3LiqPrice(marginInfo, unserializedPreview, market)

			return {
				...unserializedPreview,
				liqPrice,
				orderType,
				price: marketPrice,
				desiredFillPrice,
				leverage: wei(0),
			}
		}
		return null
	}
)

const calculateKeeperDeposit = (
	tradeType: FuturesOrderType,
	includesSLTP: boolean,
	existingOrdersCost: Wei,
	keeperBalance: Wei
) => {
	let newOrders = tradeType !== 'market' ? 1 : 0
	newOrders += includesSLTP ? 1 : 0

	const extraGasFee = newOrders * KEEPER_USD_GAS_FEE
	const combined = existingOrdersCost.add(extraGasFee)

	// Calculate required ETH based on pending orders
	const requiredDeposit =
		combined.gt(0) && keeperBalance.lt(combined) ? combined.sub(keeperBalance) : wei(0)

	// Ignore really small amounts as zap will revert
	return requiredDeposit.gt(0.00001) ? requiredDeposit : wei(0)
}

export const selectTradePanelKeeperDepositV3 = createSelector(
	selectKeeperUSDBalance,
	selectRequiredUsdForPendingOrders,
	selectCrossMarginTradePreview,
	selectSlTpTradeInputs,
	(usdBalance, reservedUsd, preview, { stopLossPriceWei, takeProfitPriceWei }) => {
		if (!preview) return wei(0)
		return calculateKeeperDeposit(
			preview.orderType,
			!!(stopLossPriceWei || takeProfitPriceWei),
			reservedUsd,
			usdBalance
		)
	}
)

export const selectCloseModalKeeperDepositV3 = createSelector(
	selectKeeperUSDBalance,
	selectRequiredUsdForPendingOrders,
	selectCloseCMPositionPreview,
	(usdBalance, reservedUsd, preview) => {
		if (!preview) return wei(0)
		return calculateKeeperDeposit(preview.orderType, false, reservedUsd, usdBalance)
	}
)

export const selectSLTPModalUsdDepositV3 = createSelector(
	selectKeeperUSDBalance,
	selectRequiredUsdForPendingOrders,
	selectAllCrossMarginSLTPOrders,
	selectEditCMPositionModalMarketKey,
	(usdBalance, reservedUsd, orders, marketKey) => {
		const stopLoss = orders.find(
			(o) => o.marketKey === marketKey && o.orderType === ConditionalOrderTypeEnum.STOP
		)
		const takeProfit = orders.find(
			(o) => o.marketKey === marketKey && o.orderType === ConditionalOrderTypeEnum.LIMIT
		)

		const hasExistingOrder = stopLoss || takeProfit

		// If there is already a SL/TP for this order then no need for extra each

		const extraGas = !hasExistingOrder ? wei(KEEPER_USD_GAS_FEE) : wei(0)
		const combined = reservedUsd.add(extraGas)

		const requiredDeposit =
			combined.gt(0) && usdBalance.lt(combined) ? combined.sub(usdBalance) : wei(0)

		return requiredDeposit
	}
)

export const selectCrossMarginAccountTransfers = createSelector(
	selectWallet,
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3,
	(wallet, network, futures) => {
		if (!wallet) return []
		const account = futures.accounts[network]?.[wallet]
		return account?.accountTransfers ?? []
	}
)

export const selectKeeperUsdDepositExceedsBal = createSelector(
	selectTradePanelKeeperDepositV3,
	selectSLTPModalUsdDepositV3,
	selectShowPositionModal,
	selectWithdrawableCrossMargin,
	selectUsdcBalance,
	(tradePanelDeposit, sltpDeposit, modal, availableMargin, walletUsdBalance) => {
		const deposit =
			modal?.type === 'futures_edit_stop_loss_take_profit' ? sltpDeposit : tradePanelDeposit
		// TODO: Allow to pull from 1CT gas tank when 1CT is hooked up and USDC paymaster supported
		return deposit.gt(walletUsdBalance.add(availableMargin))
	}
)

export const selectAssetsV3 = createSelector(
	selectBalances,
	selectCollateralBalances,
	selectPrices,
	(walletBalances, marginBalances, prices) => {
		return V3_SYNTH_MARKET_KEYS.map((marketKey) => {
			const assetV2 = v3SynthKeyToV2Asset(marketKey)
			const price =
				assetV2 === 'USDC' || assetV2 === 'sUSD' ? wei(1) : prices[assetV2]?.offChain ?? ZERO_WEI
			return {
				marketKey,
				assetName: assetV2,
				walletBalance: {
					size: walletBalances.tokenBalances[assetV2]?.balance || ZERO_WEI,
					value: price.mul(walletBalances.tokenBalances[assetV2]?.balance || ZERO_WEI),
				},
				marginBalance: {
					size: wei(marginBalances[marketKey] ?? 0),
					value: price.mul(wei(marginBalances[marketKey] ?? 0)),
				},
			}
		})
	}
)

export const selectCrossMarginAccountOrderHistory = createSelector(
	selectWallet,
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3,
	(wallet, network, futures) => {
		if (!wallet) return []
		const account = futures.accounts[network]?.[wallet]
		return account?.orderHistory ?? []
	}
)

export const selectPositionHistoryV3ForSelectedTrader = createSelector(
	selectProviderNetwork,
	(state: RootState) => state.snxPerpsV3,
	(state: RootState) => state.futures,
	(networkId, crossMargin, futures) => {
		const { selectedTrader } = futures.leaderboard
		if (!selectedTrader) return []
		const history =
			crossMargin.leaderboard.selectedTraderV3PositionHistory[networkId]?.[selectedTrader.trader] ??
			[]
		return unserializePerpsV3PositionHistory(history)
	}
)

export const selectV3TotalUnrealizedPnl = createSelector(
	selectCrossMarginPositions,
	(positions) => {
		return positions.reduce((acc, p) => acc.add(p.details?.pnl ?? 0), wei(0))
	}
)

export const selectV3PnlChartData = createSelector(
	selectCrossMarginAccountData,
	selectV3TotalUnrealizedPnl,
	(accountData, currentPnl) => {
		if (!accountData) return []
		const data = accountData.pnlSnapshots.map((s) => ({
			...s,
			total: wei(s.pnl).toNumber(),
			timestamp: s.timestamp * 1000,
		}))
		const latest = accountData.pnlSnapshots[0]
		return [
			{
				timestamp: Date.now(),
				total: currentPnl.add(latest?.pnl ?? 0).toNumber(),
			},
			...data,
		]
	}
)

export const selectV3MarginChartData = createSelector(
	selectCrossMarginAccountData,
	selectCrossMarginAvailableMargin,
	selectSelectedPortfolioTimeframe,
	(accountData, currentMargin, period) => {
		const snapshots = accountData?.marginSnapshots[period]
		if (!snapshots) return []
		const data = snapshots.map((s) => ({
			...s,
			total: wei(s.margin).toNumber(),
			timestamp: s.timestamp * 1000,
		}))
		return [
			...data,
			{
				timestamp: Date.now(),
				total: currentMargin.toNumber(),
			},
		]
	}
)

export const selectV3ChartData = createSelector(
	selectPortfolioChartType,
	selectV3PnlChartData,
	selectV3MarginChartData,
	(type, pnl, margin) => {
		return type === 'pnl' ? pnl : margin
	}
)
